import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Loader2, Scan } from "lucide-react";
import { useJscanify } from "../../../hooks/useJscanify";
import { detectDocumentCornersWithDocAligner } from "../../../utils/vault/docAligner";
import { loadImageBitmapFromDataUrl } from "../../../utils/vault/loadImageWithOrientation";
import type { CornerPoints, CornerPoint, CvMat } from "../../../types/jscanify";

interface Props {
  imageSrc: string;
  onCornersChange?: (corners: CornerPoints) => void;
  onUseOriginal?: () => void;
}

export interface DocumentCropperHandle {
  getCorners: () => CornerPoints | null;
  resetCorners: () => void;
}

type CornerKey = keyof CornerPoints;

const CORNER_KEYS: CornerKey[] = [
  "topLeftCorner",
  "topRightCorner",
  "bottomRightCorner",
  "bottomLeftCorner",
];

const CORNER_LABELS: Record<CornerKey, string> = {
  topLeftCorner: "Esquina superior izquierda",
  topRightCorner: "Esquina superior derecha",
  bottomRightCorner: "Esquina inferior derecha",
  bottomLeftCorner: "Esquina inferior izquierda",
};

/**
 * Validates that all corner points exist and have valid x,y coordinates
 * jscanify.getCornerPoints() can return objects with undefined corners
 * when the contour doesn't have exactly 4 proper points
 */
const areAllCornersValid = (
  corners: CornerPoints | null | undefined,
): corners is CornerPoints => {
  if (!corners) return false;

  return CORNER_KEYS.every((key) => {
    const corner = corners[key];
    return (
      corner !== null &&
      corner !== undefined &&
      typeof corner.x === "number" &&
      typeof corner.y === "number" &&
      !Number.isNaN(corner.x) &&
      !Number.isNaN(corner.y)
    );
  });
};

// Corner bracket styles - which borders to show for each corner when dragging
const CORNER_BRACKET_STYLES: Record<CornerKey, React.CSSProperties> = {
  topLeftCorner: {
    borderTopWidth: "3px",
    borderLeftWidth: "3px",
    borderBottomWidth: "0",
    borderRightWidth: "0",
    borderTopLeftRadius: "3px",
  },
  topRightCorner: {
    borderTopWidth: "3px",
    borderRightWidth: "3px",
    borderBottomWidth: "0",
    borderLeftWidth: "0",
    borderTopRightRadius: "3px",
  },
  bottomRightCorner: {
    borderBottomWidth: "3px",
    borderRightWidth: "3px",
    borderTopWidth: "0",
    borderLeftWidth: "0",
    borderBottomRightRadius: "3px",
  },
  bottomLeftCorner: {
    borderBottomWidth: "3px",
    borderLeftWidth: "3px",
    borderTopWidth: "0",
    borderRightWidth: "0",
    borderBottomLeftRadius: "3px",
  },
};

/**
 * Document cropper component with draggable corner handles
 * Uses jscanify for automatic document detection and perspective correction
 * Supports keyboard navigation for accessibility
 */
export const DocumentCropper = forwardRef<DocumentCropperHandle, Props>(
  ({ imageSrc, onCornersChange }, ref) => {
    const {
      scanner,
      isLoading: scannerLoading,
      error: scannerError,
    } = useJscanify();

    const [corners, setCorners] = useState<CornerPoints | null>(null);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [dragging, setDragging] = useState<CornerKey | null>(null);
    const [isDraggingRegion, setIsDraggingRegion] = useState(false);
    const [dragStart, setDragStart] = useState<CornerPoint | null>(null);
    const [loadedImageSrc, setLoadedImageSrc] = useState<string | null>(null);
    const imageLoaded = loadedImageSrc === imageSrc;

    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    // Use ref to store callback to prevent infinite loops in useEffect
    const onCornersChangeRef = useRef(onCornersChange);
    onCornersChangeRef.current = onCornersChange;

    // Notify parent when corners change (via useEffect, not inside setState updater)
    useEffect(() => {
      if (corners) {
        onCornersChangeRef.current?.(corners);
      }
    }, [corners]);

    // Store initial detected corners for reset functionality
    const initialCornersRef = useRef<CornerPoints | null>(null);

    const markImageAsLoaded = useCallback(
      (image: HTMLImageElement) => {
        if (image.naturalWidth === 0 || image.naturalHeight === 0) {
          return;
        }

        setImageSize({
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
        setLoadedImageSrc(imageSrc);
      },
      [imageSrc],
    );

    useLayoutEffect(() => {
      setImageSize({ width: 0, height: 0 });
      setCorners(null);
      initialCornersRef.current = null;

      // Cached data URLs can finish loading before React flushes passive effects.
      // Synchronize from the DOM so a missed onLoad event cannot stall detection.
      if (imageRef.current?.complete) {
        markImageAsLoaded(imageRef.current);
      }
    }, [imageSrc, markImageAsLoaded]);

    // Detect corners when the image is ready. DocAligner runs first and jscanify
    // is used only as fallback when the model cannot produce a reliable polygon.
    useEffect(() => {
      if (!imageLoaded || !imageSrc) return;
      // Scanner state can settle after DocAligner has produced editable corners.
      // Keep the user's current selection stable once initial corners exist.
      if (initialCornersRef.current) return;

      let isCancelled = false;
      let mat: CvMat | null = null;
      let contour: CvMat | null = null;
      let imageBitmap: ImageBitmap | null = null;

      const setDetectedCorners = (detectedCorners: CornerPoints) => {
        initialCornersRef.current = detectedCorners;
        setCorners(detectedCorners);
      };

      const getFallbackCorners = (
        width: number,
        height: number,
      ): CornerPoints => ({
        topLeftCorner: { x: 0, y: 0 },
        topRightCorner: { x: width, y: 0 },
        bottomRightCorner: { x: width, y: height },
        bottomLeftCorner: { x: 0, y: height },
      });

      const detectCorners = async () => {
        try {
          setCorners(null);
          const docAlignerCorners =
            await detectDocumentCornersWithDocAligner(imageSrc);

          if (isCancelled) return;

          if (areAllCornersValid(docAlignerCorners)) {
            setDetectedCorners(docAlignerCorners);
            return;
          }

          if (scannerLoading) {
            return;
          }

          // Load image with EXIF orientation applied using createImageBitmap
          // This ensures consistent orientation across all browsers
          imageBitmap = await loadImageBitmapFromDataUrl(imageSrc);

          if (isCancelled) return;

          // Create a canvas from the properly oriented image
          const canvas = document.createElement("canvas");
          canvas.width = imageBitmap.width;
          canvas.height = imageBitmap.height;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            setDetectedCorners(
              getFallbackCorners(imageBitmap.width, imageBitmap.height),
            );
            return;
          }

          ctx.drawImage(imageBitmap, 0, 0);

          // Detect corners using jscanify
          const fallbackCorners = getFallbackCorners(
            imageBitmap.width,
            imageBitmap.height,
          );

          let detectedCorners: CornerPoints;

          if (scanner && window.cv && !scannerError) {
            mat = window.cv.imread(canvas) as CvMat;
            contour = scanner.findPaperContour(mat);
          }

          if (scanner && contour) {
            const rawCorners = scanner.getCornerPoints(contour);
            // Validate that all corners have valid coordinates
            // jscanify can return undefined/invalid corners for certain contours
            detectedCorners = areAllCornersValid(rawCorners)
              ? rawCorners
              : fallbackCorners;
          } else {
            // No document detected - use full image bounds
            detectedCorners = fallbackCorners;
          }

          if (isCancelled) return;

          // Store initial corners for reset
          setDetectedCorners(detectedCorners);

          // Clean up canvas
          canvas.width = 0;
          canvas.height = 0;
        } catch (_e) {
          if (isCancelled) return;

          // Fallback on error - use imageRef to avoid dependency on imageSize state
          const imgWidth = imageRef.current?.naturalWidth ?? 0;
          const imgHeight = imageRef.current?.naturalHeight ?? 0;
          setDetectedCorners(getFallbackCorners(imgWidth, imgHeight));
        } finally {
          // Always clean up resources to prevent memory leaks
          if (contour) {
            contour.delete();
          }
          if (mat) {
            mat.delete();
          }
          if (imageBitmap) {
            imageBitmap.close();
          }
        }
      };

      detectCorners();

      return () => {
        isCancelled = true;
      };
    }, [scanner, scannerError, scannerLoading, imageLoaded, imageSrc]);

    // Handle image load
    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      markImageAsLoaded(event.currentTarget);
    };

    /**
     * Calculate the actual rendered image bounds within an element using object-fit: contain
     * getBoundingClientRect() returns element bounds, not the rendered image bounds
     */
    const getRenderedImageRect = useCallback(
      (img: HTMLImageElement): DOMRect | null => {
        const elementRect = img.getBoundingClientRect();
        const naturalAspect = img.naturalWidth / img.naturalHeight;
        const elementAspect = elementRect.width / elementRect.height;

        let renderedWidth: number;
        let renderedHeight: number;
        let offsetX: number;
        let offsetY: number;

        if (naturalAspect > elementAspect) {
          // Image is wider than element aspect - constrained by width
          renderedWidth = elementRect.width;
          renderedHeight = elementRect.width / naturalAspect;
          offsetX = 0;
          offsetY = (elementRect.height - renderedHeight) / 2;
        } else {
          // Image is taller than element aspect - constrained by height
          renderedHeight = elementRect.height;
          renderedWidth = elementRect.height * naturalAspect;
          offsetX = (elementRect.width - renderedWidth) / 2;
          offsetY = 0;
        }

        // Create a DOMRect-like object with the actual rendered image bounds
        return new DOMRect(
          elementRect.left + offsetX,
          elementRect.top + offsetY,
          renderedWidth,
          renderedHeight,
        );
      },
      [],
    );

    // Convert image coordinates to display coordinates
    const toDisplayCoords = useCallback(
      (point: CornerPoint): { left: number; top: number } => {
        if (!containerRef.current || imageSize.width === 0) {
          return { left: 0, top: 0 };
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        const imgElement = imageRef.current;

        if (!imgElement) return { left: 0, top: 0 };

        // Get the ACTUAL rendered image bounds (not element bounds)
        const imgRect = getRenderedImageRect(imgElement);
        if (!imgRect) return { left: 0, top: 0 };

        const scaleX = imgRect.width / imageSize.width;
        const scaleY = imgRect.height / imageSize.height;

        // Calculate offset from container to rendered image
        const offsetX = imgRect.left - containerRect.left;
        const offsetY = imgRect.top - containerRect.top;

        return {
          left: point.x * scaleX + offsetX,
          top: point.y * scaleY + offsetY,
        };
      },
      [imageSize, getRenderedImageRect],
    );

    // Convert display coordinates to image coordinates
    const toImageCoords = useCallback(
      (displayX: number, displayY: number): CornerPoint => {
        if (
          !containerRef.current ||
          !imageRef.current ||
          imageSize.width === 0
        ) {
          return { x: 0, y: 0 };
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        // Get the ACTUAL rendered image bounds (not element bounds)
        const imgRect = getRenderedImageRect(imageRef.current);
        if (!imgRect) return { x: 0, y: 0 };

        const scaleX = imageSize.width / imgRect.width;
        const scaleY = imageSize.height / imgRect.height;

        // Calculate offset from container to rendered image
        const offsetX = imgRect.left - containerRect.left;
        const offsetY = imgRect.top - containerRect.top;

        const x = Math.max(
          0,
          Math.min((displayX - offsetX) * scaleX, imageSize.width),
        );
        const y = Math.max(
          0,
          Math.min((displayY - offsetY) * scaleY, imageSize.height),
        );

        return { x, y };
      },
      [imageSize, getRenderedImageRect],
    );

    // Update single corner helper
    const updateCorners = useCallback(
      (cornerKey: CornerKey, newPoint: CornerPoint) => {
        setCorners((prev) => {
          if (!prev) return prev;
          return { ...prev, [cornerKey]: newPoint };
        });
      },
      [],
    );

    // Move all corners by a delta (for region dragging)
    const moveAllCorners = useCallback(
      (deltaX: number, deltaY: number) => {
        setCorners((prev) => {
          if (!prev) return prev;

          // Calculate new positions for all corners
          const newCorners: CornerPoints = {
            topLeftCorner: {
              x: Math.max(
                0,
                Math.min(imageSize.width, prev.topLeftCorner.x + deltaX),
              ),
              y: Math.max(
                0,
                Math.min(imageSize.height, prev.topLeftCorner.y + deltaY),
              ),
            },
            topRightCorner: {
              x: Math.max(
                0,
                Math.min(imageSize.width, prev.topRightCorner.x + deltaX),
              ),
              y: Math.max(
                0,
                Math.min(imageSize.height, prev.topRightCorner.y + deltaY),
              ),
            },
            bottomRightCorner: {
              x: Math.max(
                0,
                Math.min(imageSize.width, prev.bottomRightCorner.x + deltaX),
              ),
              y: Math.max(
                0,
                Math.min(imageSize.height, prev.bottomRightCorner.y + deltaY),
              ),
            },
            bottomLeftCorner: {
              x: Math.max(
                0,
                Math.min(imageSize.width, prev.bottomLeftCorner.x + deltaX),
              ),
              y: Math.max(
                0,
                Math.min(imageSize.height, prev.bottomLeftCorner.y + deltaY),
              ),
            },
          };

          return newCorners;
        });
      },
      [imageSize],
    );

    // Handle mouse/touch events for dragging
    const handlePointerDown =
      (cornerKey: CornerKey) => (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(cornerKey);
        try {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
        } catch {
          // Pointer capture may fail in some edge cases, continue without it
        }
      };

    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!corners || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const displayX = e.clientX - containerRect.left;
        const displayY = e.clientY - containerRect.top;
        const imageCoords = toImageCoords(displayX, displayY);

        // Handle region dragging (moving all corners)
        if (isDraggingRegion && dragStart) {
          const deltaX = imageCoords.x - dragStart.x;
          const deltaY = imageCoords.y - dragStart.y;
          moveAllCorners(deltaX, deltaY);
          setDragStart(imageCoords);
          return;
        }

        // Handle single corner dragging
        if (dragging) {
          updateCorners(dragging, imageCoords);
        }
      },
      [
        dragging,
        isDraggingRegion,
        dragStart,
        corners,
        toImageCoords,
        updateCorners,
        moveAllCorners,
      ],
    );

    const handlePointerUp = (e: React.PointerEvent) => {
      if (dragging) {
        try {
          (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          // Release may fail if capture wasn't successful
        }
        setDragging(null);
      }
      if (isDraggingRegion) {
        setIsDraggingRegion(false);
        setDragStart(null);
      }
    };

    // Handle region drag start (clicking inside the polygon)
    const handleRegionPointerDown = (e: React.PointerEvent) => {
      if (!corners || !containerRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const containerRect = containerRef.current.getBoundingClientRect();
      const displayX = e.clientX - containerRect.left;
      const displayY = e.clientY - containerRect.top;
      const imageCoords = toImageCoords(displayX, displayY);

      setIsDraggingRegion(true);
      setDragStart(imageCoords);

      try {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // Pointer capture may fail in some edge cases
      }
    };

    // Keyboard navigation for accessibility
    const handleKeyDown =
      (cornerKey: CornerKey) => (e: React.KeyboardEvent) => {
        if (!corners) return;

        const step = e.shiftKey ? 10 : 1;
        const current = corners[cornerKey];

        let deltaX = 0;
        let deltaY = 0;

        switch (e.key) {
          case "ArrowLeft":
            deltaX = -step;
            break;
          case "ArrowRight":
            deltaX = step;
            break;
          case "ArrowUp":
            deltaY = -step;
            break;
          case "ArrowDown":
            deltaY = step;
            break;
          default:
            return;
        }

        e.preventDefault();
        const newPoint: CornerPoint = {
          x: Math.max(0, Math.min(imageSize.width, current.x + deltaX)),
          y: Math.max(0, Math.min(imageSize.height, current.y + deltaY)),
        };
        updateCorners(cornerKey, newPoint);
      };

    // Generate SVG polygon points
    const getPolygonPoints = (): string => {
      if (!corners) return "";

      const points = CORNER_KEYS.map((key) => {
        const pos = toDisplayCoords(corners[key]);
        return `${pos.left},${pos.top}`;
      });

      return points.join(" ");
    };

    // Reset corners to initial detected values
    const resetCorners = useCallback(() => {
      if (initialCornersRef.current) {
        setCorners(initialCornersRef.current);
      }
    }, []);

    // Expand selection to full image bounds
    const selectFullImage = useCallback(() => {
      if (imageSize.width === 0 || imageSize.height === 0) return;
      setCorners({
        topLeftCorner: { x: 0, y: 0 },
        topRightCorner: { x: imageSize.width, y: 0 },
        bottomRightCorner: { x: imageSize.width, y: imageSize.height },
        bottomLeftCorner: { x: 0, y: imageSize.height },
      });
    }, [imageSize]);

    // Expose methods via ref using useImperativeHandle
    useImperativeHandle(
      ref,
      () => ({
        getCorners: () => corners,
        resetCorners,
      }),
      [corners, resetCorners],
    );

    return (
      <div
        ref={containerRef}
        className="relative w-full select-none"
        style={{ touchAction: "none" }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        role="application"
        aria-label="Editor de recorte de documento"
      >
        {/* Image */}
        <img
          ref={imageRef}
          src={imageSrc}
          alt="Documento a recortar"
          className="w-full h-auto max-h-[calc(90vh-12rem)] object-contain"
          onLoad={handleImageLoad}
          draggable={false}
        />

        {/* Select full image button */}
        {corners && imageLoaded && (
          <button
            type="button"
            onClick={selectFullImage}
            className="absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-md bg-white/90 border border-gray-300 text-gray-600 hover:bg-white hover:text-blue-600 hover:border-blue-400 transition-colors shadow-sm"
            title="Seleccionar imagen completa"
            aria-label="Seleccionar imagen completa"
          >
            <Scan className="h-5 w-5" />
          </button>
        )}

        {/* SVG Overlay for polygon - draggable to move entire selection */}
        {corners && imageLoaded && (
          <svg
            className="absolute top-0 left-0 w-full h-full"
            style={{ overflow: "visible" }}
            aria-hidden="true"
          >
            <polygon
              points={getPolygonPoints()}
              fill="rgba(24, 144, 255, 0.15)"
              stroke="#1890ff"
              strokeWidth="2"
              strokeDasharray="8,4"
              className={isDraggingRegion ? "cursor-grabbing" : "cursor-move"}
              style={{ pointerEvents: "fill" }}
              onPointerDown={handleRegionPointerDown}
            />
          </svg>
        )}

        {/* Draggable corner handles with accessibility */}
        {corners &&
          imageLoaded &&
          CORNER_KEYS.map((key) => {
            const pos = toDisplayCoords(corners[key]);
            const isThisCornerDragging = dragging === key;

            return (
              <div
                key={key}
                role="slider"
                tabIndex={0}
                aria-label={CORNER_LABELS[key]}
                aria-valuetext={`x: ${Math.round(corners[key].x)}, y: ${Math.round(corners[key].y)}`}
                aria-describedby="corner-instructions"
                className={`absolute cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                  isThisCornerDragging
                    ? "w-5 h-5 bg-transparent border-blue-500"
                    : "w-7 h-7 rounded-full bg-blue-500 border-white"
                }`}
                style={{
                  left: pos.left,
                  top: pos.top,
                  transform: `translate(-50%, -50%) scale(${isThisCornerDragging ? 1.5 : 1})`,
                  boxShadow: isThisCornerDragging
                    ? "none"
                    : "0 2px 8px rgba(0, 0, 0, 0.3)",
                  zIndex: isThisCornerDragging ? 20 : 10,
                  borderStyle: "solid",
                  // Apply bracket borders when dragging, full border when not
                  ...(isThisCornerDragging
                    ? CORNER_BRACKET_STYLES[key]
                    : { borderWidth: "3px" }),
                  // Only transition visual effects, not position
                  transition:
                    "transform 150ms, box-shadow 150ms, background-color 150ms, border-color 150ms",
                }}
                onPointerDown={handlePointerDown(key)}
                onKeyDown={handleKeyDown(key)}
              />
            );
          })}

        {/* Screen reader instructions */}
        <div id="corner-instructions" className="sr-only">
          Usa las flechas del teclado para mover la esquina. Mantén Shift para
          movimientos más grandes.
        </div>

        {/* Instructions overlay (shown briefly) */}
        {!corners && imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Loader2 className="size-6 animate-spin text-white" />
            <p className="ml-3 text-white">Detectando documento...</p>
          </div>
        )}
      </div>
    );
  },
);

DocumentCropper.displayName = "DocumentCropper";
